/**
 * noteUtils.js — Pure utility functions for note title generation, billing codes, and medical text processing.
 * Extracted from App.js to reduce monolith size.
 */

// Medical spell-check map for common misspellings/variants
export const MEDICAL_SPELL_FIX = {
  'anual': 'Annual', 'annuall': 'Annual', 'annnual': 'Annual',
  'wellnes': 'Wellness', 'wellnss': 'Wellness', 'welness': 'Wellness',
  'exma': 'Exam', 'exame': 'Exam',
  'headahce': 'Headache', 'headace': 'Headache', 'hedache': 'Headache',
  'abdomenal': 'Abdominal', 'abdomnal': 'Abdominal',
  'musculosketal': 'Musculoskeletal', 'musculoskelatal': 'Musculoskeletal',
  'hypertenson': 'Hypertension', 'hypertenison': 'Hypertension',
  'diabeties': 'Diabetes', 'diabetis': 'Diabetes', 'diabtes': 'Diabetes',
  'depresion': 'Depression', 'depresssion': 'Depression',
  'anxeity': 'Anxiety', 'anxeiety': 'Anxiety', 'anxity': 'Anxiety',
  'referal': 'Referral', 'referall': 'Referral', 'refferal': 'Referral',
  'medicaiton': 'Medication', 'medicaton': 'Medication', 'medcation': 'Medication',
  'deployement': 'Deployment', 'deploymnet': 'Deployment',
  'evalution': 'Evaluation', 'evaluaiton': 'Evaluation',
  'procedrue': 'Procedure', 'proceedure': 'Procedure',
  'follw': 'Follow', 'folow': 'Follow',
  'restricitons': 'Restrictions', 'restrictons': 'Restrictions',
  'promtoion': 'Promotion', 'promoton': 'Promotion',
  'laceraiton': 'Laceration', 'laceratoin': 'Laceration',
  'nausea': 'Nausea', 'nausae': 'Nausea',
  'injurey': 'Injury', 'injuiry': 'Injury',
  'paitent': 'Patient', 'patinet': 'Patient', 'pateint': 'Patient',
  'chronic': 'Chronic', 'chronc': 'Chronic',
  'consult': 'Consult', 'cosult': 'Consult', 'consultaiton': 'Consultation',
};

// Medical acronyms that must stay uppercase
export const MEDICAL_ACRONYMS = new Set([
  'HTN', 'DM', 'GERD', 'COPD', 'URI', 'UTI', 'MSK', 'BH', 'PHA', 'BMI',
  'PTSD', 'TBI', 'DVT', 'PE', 'CHF', 'CAD', 'MI', 'CVA', 'TIA', 'CKD',
  'AKI', 'ADHD', 'OCD', 'IBS', 'OSA', 'RA', 'OA', 'ACL', 'MRI', 'CT',
  'CBC', 'CMP', 'TSH', 'HGB', 'A1C', 'LDL', 'HDL', 'EKG', 'ECG', 'EMG',
  'SRP', 'SRD', 'VH', 'PGE', 'PHQ', 'GAD', 'PCM', 'MTF', 'LOD', 'MEB',
  'PEB', 'IDES', 'DA', 'DD', 'NOS', 'PMH', 'HPI', 'ROS', 'ICD', 'CPT',
  'SOAP', 'ED', 'ER', 'ICU', 'OR', 'PT', 'OT', 'BMP', 'WBC', 'RBC',
  'BP', 'HR', 'RR', 'SPO2', 'IV', 'IM', 'PO', 'PRN', 'BID', 'TID', 'QID',
  'II', 'III', 'F/U',
]);

// Smart title case: preserves acronyms, fixes spelling, capitalizes properly
export const smartTitleCase = (str) => {
  if (!str) return '';
  return str.split(/(\s+|[-/,])/).map(word => {
    const trimmed = word.trim();
    if (!trimmed || /^[-/,\s]+$/.test(word)) return word;
    const lower = trimmed.toLowerCase();
    if (MEDICAL_SPELL_FIX[lower]) return MEDICAL_SPELL_FIX[lower];
    const upper = trimmed.toUpperCase();
    if (MEDICAL_ACRONYMS.has(upper)) return upper;
    if (trimmed === upper && trimmed.length <= 5 && trimmed.length >= 2) return upper;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }).join('');
};

// Generate additional billing codes based on ICD-10 diagnoses
export const getAdditionalBillingCodes = (result) => {
  if (!result || !result.icd10_suggestions) return [];
  const codes = [];
  const icd10s = result.icd10_suggestions.map(c => typeof c === 'object' ? c.code : c).join(' ').toUpperCase();

  if (icd10s.match(/E66|Z68|R63\.4/)) {
    codes.push({ code: '2001F', description: 'Weight recorded' });
    codes.push({ code: '3008F', description: 'BMI documented' });
  }
  if (icd10s.match(/I1[0-5]|R03/)) {
    codes.push({ code: '2000F', description: 'Blood pressure measured' });
    if (!codes.find(c => c.code === '3008F')) {
      codes.push({ code: '3008F', description: 'BMI documented' });
    }
  }
  if (icd10s.match(/F3[2-3]|F4[1-3]/)) {
    codes.push({ code: '96127', description: 'PHQ-9/GAD-7 screening' });
  }
  if (icd10s.match(/Z00\.12|H52/)) {
    codes.push({ code: '99173', description: 'Visual acuity screening' });
  }
  if (icd10s.match(/F90|G47|F51/)) {
    codes.push({ code: '96160', description: 'Health risk assessment' });
  }
  return codes;
};

// Generate smart note title from form state
export const generateNoteTitle = ({
  result, chiefComplaint, reasonForVisit, customReasonForVisit,
  notes, visitType, patientLabel, history, REASON_FOR_VISIT_OPTIONS,
  dosOverride, dosTimeOverride,
}) => {
  if (!result && !chiefComplaint && !reasonForVisit && !customReasonForVisit && !notes) return '';

  let prefix;
  if (visitType === 'virtual' || visitType === 'results_review_virtual') prefix = 'VH Note';
  else if (visitType === 'in_person_counseling') prefix = 'PGE VH Note';
  else prefix = 'Office Clinic Note';

  let topic = '';

  // PRIORITY 0 (NEW Feb 15, 2026 — user feedback "titles are real lazy"):
  // If the LLM has actually generated content with ICD-10 suggestions, those
  // descriptions are the RICHEST signal for the title — they reflect the
  // ACTUAL A/P, not just the dictated complaint. Use the top 2 dx descriptions.
  if (result && Array.isArray(result.icd10_suggestions) && result.icd10_suggestions.length > 0) {
    const dxList = [];
    for (const code of result.icd10_suggestions.slice(0, 4)) {
      if (!code) continue;
      // Description format varies — accept both .desc and .description / .description_short
      let desc = typeof code === 'object'
        ? (code.description_short || code.desc || code.description || '')
        : '';
      if (!desc) continue;
      // Strip the ICD-10 code prefix if it was concatenated, and any "encounter for"
      // boilerplate that doesn't add meaning.
      desc = String(desc)
        .replace(/^[A-Z]\d{1,2}\.?\d*\s*[-:]\s*/i, '')
        .replace(/^encounter\s+for\s+/i, '')
        .replace(/\bunspecified\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      // First-sentence / first-clause only — descriptions can be very long
      desc = desc.split(/[,;(]/)[0].trim();
      // Skip Z71.89 / Z00.00 administrative codes — those aren't real topics
      const codeStr = typeof code === 'object' ? (code.code || '') : '';
      if (/^Z71|^Z00|^Z02/i.test(codeStr)) continue;
      if (desc.length > 2 && desc.length < 45 && !dxList.includes(desc)) {
        dxList.push(desc);
      }
      if (dxList.length >= 2) break;
    }
    if (dxList.length > 0) topic = dxList.map(d => smartTitleCase(d)).join(', ');
  }

  // PRIORITY 1: Chief complaint (fallback when no ICD-10s yet)
  if (!topic && chiefComplaint && chiefComplaint.trim() && chiefComplaint.trim().toLowerCase() !== 'plan') {
    topic = chiefComplaint.trim()
      .split(',').slice(0, 2).map(c => c.trim())
      .filter(c => c.toLowerCase() !== 'plan' && c.length > 2)
      .map(c => smartTitleCase(c))
      .join(', ');
  }

  // SECOND: Reason for Visit label
  // Feb 18, 2026 — Multi-reason aware. reasonForVisit can now be a comma-
  // separated list (e.g. "annual_exam,lab_review,imaging_review"). We
  // intelligently MERGE the labels rather than dumping the raw list:
  //   • Single:   "Lab Review"
  //   • Multi:    "Lab and Imaging Review"   (shared "Review" suffix collapsed)
  //   • Multi:    "Annual Wellness Exam + Lab Review"   (no common suffix)
  if (!topic && reasonForVisit && reasonForVisit !== 'other') {
    const values = String(reasonForVisit).split(',').map(v => v.trim()).filter(v => v && v !== 'other');
    if (values.length > 0) {
      const labels = values.map(v => {
        const opt = REASON_FOR_VISIT_OPTIONS.find(o => o.value === v);
        return opt ? opt.label : smartTitleCase(v.split('_').join(' '));
      });
      if (labels.length === 1) {
        topic = labels[0];
      } else {
        // Try to collapse a shared trailing word ("Lab Review" + "Imaging Review"
        // → "Lab and Imaging Review"). Walk back word-by-word; keep stripping
        // while every label still ends with that word.
        const wordLists = labels.map(l => l.trim().split(/\s+/));
        const trailing = [];
        let canTrim = true;
        while (canTrim) {
          const lastWords = wordLists.map(w => w[w.length - 1]?.toLowerCase());
          const allSame = lastWords.every(w => w && w === lastWords[0]) && wordLists.every(w => w.length > 1);
          if (allSame) {
            // Pop and remember the original casing from the first label
            trailing.unshift(wordLists[0][wordLists[0].length - 1]);
            wordLists.forEach(w => w.pop());
          } else {
            canTrim = false;
          }
        }
        const heads = wordLists.map(w => w.join(' '));
        let merged;
        if (heads.length === 2) {
          merged = `${heads[0]} and ${heads[1]}`;
        } else {
          merged = heads.slice(0, -1).join(', ') + ', and ' + heads[heads.length - 1];
        }
        topic = trailing.length > 0
          ? `${merged} ${trailing.join(' ')}`
          : labels.join(' + ');  // fall back to the plus-join for truly disjoint reasons
      }
    }
  }
  if (!topic && customReasonForVisit && customReasonForVisit.trim()) {
    topic = smartTitleCase(customReasonForVisit.trim());
  }

  // THIRD: Quick HPI phrase extraction
  if (!topic && notes && notes.trim().length > 20) {
    const complaintsMatch = notes.match(/(?:presents? with|complain(?:s|ing)? of|here for|reports?|experiencing|annual|wellness)\s+([^.,\n]{3,40})/i);
    if (complaintsMatch && complaintsMatch[1]) {
      let extracted = complaintsMatch[1].trim().replace(/^\s*(a|an|the)\s+/i, '');
      if (extracted.length > 3) topic = smartTitleCase(extracted);
    }
  }

  // SMART PRIOR HX
  let priorHx = '';
  if (notes && notes.trim().length > 20) {
    const pmhPatterns = [
      /(?:PMH|Past Medical History|Medical History|Hx|History)[:\s]+([^\n]{5,80})/i,
      /(?:significant for|positive for|notable for)[:\s]+([^\n.]{5,60})/i,
      /(?:prior (?:hx|history) (?:of|includes?))[:\s]*([^\n.]{5,60})/i,
    ];
    for (const pattern of pmhPatterns) {
      const match = notes.match(pattern);
      if (match && match[1]) {
        let hxRaw = match[1].trim().split(/[.;\n]/)[0].trim();
        const conditions = hxRaw.split(/[,/&]+/)
          .map(c => c.trim())
          .filter(c => c.length > 1 && c.length < 30)
          .slice(0, 3)
          .map(c => smartTitleCase(c));
        if (conditions.length > 0) priorHx = conditions.join(', ');
        break;
      }
    }
  }

  // Check history entries for prior dx
  if (!priorHx && patientLabel && history && history.length > 0) {
    const priorVisits = history.filter(h =>
      h.patient_label === patientLabel && h.icd10_suggestions && h.icd10_suggestions.length > 0
    );
    if (priorVisits.length > 0) {
      const priorDx = [];
      for (const visit of priorVisits.slice(0, 3)) {
        for (const code of (visit.icd10_suggestions || []).slice(0, 2)) {
          const desc = typeof code === 'object' ? (code.desc || '').split(',')[0].split('(')[0].trim() : '';
          if (desc && desc.length > 2 && !priorDx.includes(desc)) priorDx.push(desc);
        }
        if (priorDx.length >= 3) break;
      }
      if (priorDx.length > 0) priorHx = priorDx.slice(0, 3).join(', ');
    }
  }

  // If DOS was manually overridden, update the time in patientLabel
  let effectiveLabel = patientLabel || '';
  if (dosOverride || dosTimeOverride) {
    // Build date/time string from override
    const datePart = dosOverride || '';
    const timePart = dosTimeOverride || '';
    let dosStr = '';
    if (datePart) {
      const [y, m, d] = datePart.split('-');
      dosStr = `${m}/${d}/${y?.slice(2)}`;
    }
    if (timePart) {
      dosStr += (dosStr ? ' ' : '') + timePart;
    }
    if (dosStr) {
      // Replace time in patientLabel or append it
      const timePattern = /\d{1,2}:\d{2}(?:\s*(?:AM|PM))?|\d{4}\b/i;
      if (effectiveLabel && timePattern.test(effectiveLabel)) {
        effectiveLabel = effectiveLabel.replace(timePattern, dosStr);
      } else if (effectiveLabel) {
        effectiveLabel = `${effectiveLabel} ${dosStr}`;
      } else {
        effectiveLabel = dosStr;
      }
    }
  }

  const labelPrefix = effectiveLabel ? `${effectiveLabel} | ` : '';
  if (topic) {
    topic = smartTitleCase(topic.replace(/["']/g, '').trim());
    const hxSuffix = priorHx ? ` (Hx: ${priorHx})` : '';
    return `${labelPrefix}${prefix} - ${topic}${hxSuffix}`;
  }
  return `${labelPrefix}${prefix}`;
};

// Auto-detect reason for visit keyword map
export const REASON_KEYWORD_MAP = [
  { value: 'annual_health_promo', keywords: ['wellness exam and health promotion', 'annual and health promotion', 'health promotion referral'] },
  { value: 'annual_lab_review', keywords: ['wellness exam and lab', 'annual and lab review', 'annual wellness exam lab'] },
  { value: 'annual_exam', keywords: ['annual wellness', 'annual exam', 'wellness exam', 'pha ', 'preventive', 'yearly physical', 'periodic health'] },
  { value: 'sick_call', keywords: ['sick call', 'upper respiratory infection', 'uri visit', 'cold symptoms', 'acute illness'] },
  { value: 'msk', keywords: ['back pain', 'knee pain', 'shoulder pain', 'musculoskeletal', ' msk ', 'joint pain', 'hip pain', 'neck pain', 'sprain', 'strain', 'low back'] },
  { value: 'mental_health', keywords: ['depression', 'anxiety', 'ptsd', 'mental health', 'behavioral health', 'insomnia', 'suicidal', 'phq-9', 'gad-7'] },
  { value: 'lab_review', keywords: ['lab review', 'lab results', 'bloodwork', ' cbc ', ' cmp ', ' a1c', 'lipid panel'] },
  { value: 'imaging_review', keywords: ['imaging review', 'x-ray', 'xray', ' mri ', 'ct scan', 'ultrasound results'] },
  { value: 'medication_refill', keywords: ['refill', 'medication refill', 'med refill', 'prescription renewal'] },
  { value: 'deployment_health', keywords: ['deployment', ' srd ', 'pre-deploy', 'post-deploy'] },
  { value: 'profile_eval', keywords: ['profile eval', 'temporary profile', 'permanent profile', 'e-profile'] },
  { value: 'consult_follow_up', keywords: ['consult follow', 'follow up', 'follow-up visit', 'followup', 'return visit'] },
  { value: 'chronic_care', keywords: ['chronic care', 'diabetes management', 'hypertension management', 'copd management', 'asthma management'] },
  { value: 'procedure', keywords: ['procedure', 'injection', 'i&d', 'incision and drainage', 'laceration', 'suture', 'biopsy'] },
];

// ── DOB → AGE EXTRACTION ──────────────────────────────────────────────────
// Parses spoken DOB phrases providers use at the start of every call.
// Returns the calculated age (integer years) or null if no DOB is found.
const _MONTH_NAMES = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};
const _normalizeYear = (yearStr) => {
  let y = parseInt(yearStr, 10);
  if (Number.isNaN(y)) return null;
  if (y < 100) {
    const cy = new Date().getFullYear() % 100;
    y = (y > cy) ? 1900 + y : 2000 + y;
  }
  return y;
};
const _calcAge = (year, month, day) => {
  if (!year || !month || !day) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const today = new Date();
  let age = today.getFullYear() - year;
  const m = today.getMonth() + 1 - month;
  if (m < 0 || (m === 0 && today.getDate() < day)) age--;
  if (age < 0 || age > 120) return null;
  return age;
};
export const extractAgeFromDob = (notes) => {
  if (!notes || typeof notes !== 'string') return null;
  const text = notes;
  // 1. Numeric DOB with marker — "DOB 5/12/1985", "born 5-12-85"
  const numericRe = /\b(?:dob|d\.o\.b\.?|date\s+of\s+birth|born(?:\s+on)?)\s*[:\-]?\s*(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})\b/i;
  let m = text.match(numericRe);
  if (m) {
    const age = _calcAge(_normalizeYear(m[3]), parseInt(m[1], 10), parseInt(m[2], 10));
    if (age !== null) return age;
  }
  // 2. Month-name DOB — "DOB May 12 1985"
  const monthRe = /\b(?:dob|d\.o\.b\.?|date\s+of\s+birth|born(?:\s+on)?)\s*[:\-]?\s*([A-Za-z]{3,9})\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{2,4})\b/i;
  m = text.match(monthRe);
  if (m) {
    const age = _calcAge(_normalizeYear(m[3]), _MONTH_NAMES[m[1].toLowerCase()], parseInt(m[2], 10));
    if (age !== null) return age;
  }
  // 3. Day-first month-name — "DOB 12 May 1985"
  const dfRe = /\b(?:dob|d\.o\.b\.?|date\s+of\s+birth|born(?:\s+on)?)\s*[:\-]?\s*(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9}),?\s+(\d{2,4})\b/i;
  m = text.match(dfRe);
  if (m) {
    const age = _calcAge(_normalizeYear(m[3]), _MONTH_NAMES[m[2].toLowerCase()], parseInt(m[1], 10));
    if (age !== null) return age;
  }
  // 4. Standalone MM/DD/YYYY — last resort, only birth-year-plausible
  const sRe = /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](19\d{2}|20[0-1]\d)\b/;
  m = text.match(sRe);
  if (m) {
    const year = parseInt(m[3], 10);
    if (year <= new Date().getFullYear() - 10) {
      const age = _calcAge(year, parseInt(m[1], 10), parseInt(m[2], 10));
      if (age !== null) return age;
    }
  }
  return null;
};

// Extract patient info from notes
export const extractPatientInfo = (notes) => {
  const info = {};
  const rankMatch = notes.match(/\b(Mr\.|Mrs\.|Ms\.|Miss|Dr\.|PVT|PV2|PFC|SPC|CPL|SGT|SSG|SFC|MSG|1SG|SGM|CSM|2LT|1LT|CPT|MAJ|LTC|COL|BG|MG|LTG|GEN)\b/i);
  if (rankMatch) info.rank = rankMatch[1];

  // Mar 4, 2026 (v6.9.5) — separator `[\s\-]*` (was `\s*`) so hyphenated
  // dictation-transcription forms like "25-year-old" also match, plus new
  // fallback shorthand "25 F" / "25 M" for high-tempo voice workflows.
  const ageMatch = notes.match(/\b(\d{1,3})[\s\-]*(?:yo|y\.o\.|y\/o|year[s]?[\s\-]*old)\b/i);
  if (ageMatch) {
    const extractedAge = parseInt(ageMatch[1]);
    if (extractedAge >= 0 && extractedAge <= 120) info.age = ageMatch[1];
  }
  // DOB → age fallback when no explicit "yo" mentioned (per provider workflow:
  // every call opens with the patient's DOB).
  if (!info.age) {
    const dobAge = extractAgeFromDob(notes);
    if (dobAge !== null) info.age = String(dobAge);
  }
  // Bare shorthand fallback: "25 F", "32M", "40 f active duty" — only when
  // no explicit "yo" was already parsed. Restricted to the first 80 chars
  // of the note (demographic prefix zone) to avoid false-matching later
  // vitals. Negative lookbehind `(?<!\.)` rejects the decimal-tail of a
  // temperature ("98.6 F" would otherwise match age=6, gender=female).
  if (!info.age) {
    const opening = notes.substring(0, 80);
    const bareMatch = opening.match(/(?<![.\/])\b(\d{1,3})\s*([MF])\b(?![a-z])/);
    if (bareMatch) {
      const bareAge = parseInt(bareMatch[1]);
      if (bareAge >= 0 && bareAge <= 120) {
        info.age = bareMatch[1];
        info.gender = bareMatch[2].toUpperCase() === 'M' ? 'male' : 'female';
      }
    }
  }

  // Reverse-order phrasing: "female age 27" / "male, age 32". Handled
  // separately because it puts age in group 2 vs group 1 in other patterns.
  const reverseMatch = notes.match(/\b(male|female)[,\s]+age\s+(\d{1,3})\b/i);
  if (reverseMatch) {
    if (!info.gender) info.gender = reverseMatch[1].toLowerCase();
    if (!info.age) {
      const a = parseInt(reverseMatch[2]);
      if (a >= 0 && a <= 120) info.age = reverseMatch[2];
    }
  }

  if (!info.gender) {
    const genderMatch = notes.match(/\b(\d{1,3})[\s\-]*(?:yo|y\.o\.|y\/o|year[s]?[\s\-]*old)\s+(male|female)\b/i)
      || notes.match(/\b(male|female)\s+(\d{1,3})\s*(?:yo|y\.o\.|y\/o)\b/i)
      || notes.match(/\b(male|female)\b/i);
    if (genderMatch) {
      // Pattern 1: [1]=age [2]=gender. Pattern 2 & 3: [1]=gender.
      const rawA = (genderMatch[2] || '').toUpperCase();
      const rawB = (genderMatch[1] || '').toUpperCase();
      const genderRaw = (rawA === 'MALE' || rawA === 'FEMALE') ? rawA
        : (rawB === 'MALE' || rawB === 'FEMALE') ? rawB : '';
      info.gender = (genderRaw === 'MALE') ? 'male' : (genderRaw === 'FEMALE') ? 'female' : undefined;
    }
  }

  const statusMatch = notes.match(/\b(active duty|active-duty|veteran|retiree|dependent|civilian)\b/i)
    || notes.match(/\b(\d{1,3})\s*(?:yo|y\.o\.|y\/o)\s+(?:male|female)\s+(ADSM|AD)\b/i)
    || notes.match(/\b(ADSM)\b/);
  if (statusMatch) {
    const s = (statusMatch[2] || statusMatch[1]).toLowerCase();
    if (s === 'ad' || s === 'active duty' || s === 'active-duty' || s === 'adsm') info.status = 'active_duty';
    else if (s === 'veteran') info.status = 'veteran';
    else if (s === 'retiree') info.status = 'retiree';
    else if (s === 'dependent') info.status = 'dependent';
    else if (s === 'civilian') info.status = 'civilian';
  }

  // Chief complaint
  const explicitCC = notes.match(/(?:cc|chief complaint|c\/o|complains of|presents with|here for|presenting with)[:\s]+([^.,\n]+)/i);
  if (explicitCC && explicitCC[1]) {
    const cc = explicitCC[1].trim()
      .replace(/^(the|a|an)\s+/i, '')
      .replace(/\s+/g, ' ')
      .substring(0, 80);
    if (cc.length > 2) info.chiefComplaint = cc.toLowerCase();
  }

  return info;
};

/**
 * Extract appointment time from dictated notes.
 * Detects patterns like:
 *   - "10:30 AM patient", "8:30 AM appointment", "seen at 10:30"
 *   - "0830 appointment", "arrived 1430"
 *   - "10:30 a.m.", "2:30pm"
 *   - "at 10:30", "at 0830"
 * Returns { time24: "10:30", display: "10:30 AM" } or null
 */
export const extractAppointmentTime = (notes) => {
  if (!notes || notes.length < 5) return null;

  // Pattern 1: 12-hour time (e.g., "10:30 AM", "8:30am", "2:30 p.m.", "10:30 a.m.")
  const time12Match = notes.match(
    /\b(\d{1,2}):(\d{2})\s*(a\.?m\.?|p\.?m\.?)\b/i
  );
  if (time12Match) {
    let hours = parseInt(time12Match[1]);
    const minutes = parseInt(time12Match[2]);
    const period = time12Match[3].replace(/\./g, '').toUpperCase();
    if (hours < 1 || hours > 12 || minutes > 59) return null;
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return formatTimeResult(hours, minutes);
  }

  // Pattern 2: Military time with context (e.g., "at 0830", "arrived 1430", "seen 0900")
  const milMatch = notes.match(
    /\b(?:at|arrived|seen|visit|appointment|appt|check.?in|checked.?in|patient)\s+(\d{4})\b/i
  );
  if (milMatch) {
    const hours = parseInt(milMatch[1].substring(0, 2));
    const minutes = parseInt(milMatch[1].substring(2, 4));
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return formatTimeResult(hours, minutes);
    }
  }

  // Pattern 3: Standalone military time at start of notes or after common prefixes
  // Only match 4-digit military times that look like valid appointment hours (0600-1800)
  const standaloneMilMatch = notes.match(
    /(?:^|\n)\s*(\d{4})\s+(?:hrs?|hours?|patient|pt|appt|visit|sick|annual|wellness|follow)/i
  );
  if (standaloneMilMatch) {
    const hours = parseInt(standaloneMilMatch[1].substring(0, 2));
    const minutes = parseInt(standaloneMilMatch[1].substring(2, 4));
    if (hours >= 6 && hours <= 18 && minutes >= 0 && minutes <= 59) {
      return formatTimeResult(hours, minutes);
    }
  }

  // Pattern 4: "at HH:MM" without AM/PM (assume based on value)
  const atTimeMatch = notes.match(
    /\b(?:at|around|approximately|~)\s+(\d{1,2}):(\d{2})\b/i
  );
  if (atTimeMatch) {
    let hours = parseInt(atTimeMatch[1]);
    const minutes = parseInt(atTimeMatch[2]);
    if (minutes > 59) return null;
    // Smart AM/PM inference: times 1-6 likely PM in clinic setting, 7-12 likely AM
    if (hours >= 1 && hours <= 6) hours += 12;
    if (hours >= 7 && hours <= 23) {
      return formatTimeResult(hours, minutes);
    }
  }

  return null;
};

// Helper: format hours/minutes into { time24, display }
const formatTimeResult = (hours, minutes) => {
  const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  const display = `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  return { time24, display };
};

/**
 * extractPEFindings — Scan dictation for physical-exam findings and return a
 * compact, comma-separated string suitable for the "Except / Findings" input.
 *
 * Strategy: pattern-match well-known PE descriptors and grab a tight 2–6 word
 * neighborhood around each hit. Returns '' if nothing detected.
 *
 * Designed to be conservative — better to miss a finding than hallucinate one.
 * The provider can always edit the result.
 */
export const extractPEFindings = (notes) => {
  if (!notes || typeof notes !== 'string' || notes.length < 20) return '';
  const text = notes.replace(/\s+/g, ' ').trim();
  // Lowercase for matching, but preserve original case for output snippets
  const lower = text.toLowerCase();
  const findings = [];
  const seen = new Set();

  const push = (raw) => {
    const cleaned = raw.replace(/\s+/g, ' ').replace(/[.,;:!?]+$/, '').trim();
    if (!cleaned || cleaned.length < 3 || cleaned.length > 80) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    // Suppress overlapping: skip if any existing finding fully contains this
    // (or vice-versa) — keeps the longer, more informative one.
    for (const existing of Array.from(seen)) {
      if (existing.includes(key)) return;
      if (key.includes(existing)) {
        seen.delete(existing);
        const idx = findings.findIndex(f => f.toLowerCase() === existing);
        if (idx >= 0) findings.splice(idx, 1);
      }
    }
    seen.add(key);
    findings.push(cleaned);
  };

  // Patterns: keyword regex → grab a window around the match.
  // Use word boundaries to avoid false hits inside other words.
  const patterns = [
    // Tenderness / palpation
    /\b(?:ttp|tender(?:ness)? to palpation|tender(?:ness)?|pain(?:ful)? on palpation|painful palpation)\b[\s\S]{0,40}?(?=[.,;]|\sand\s|$)/gi,
    // Range of motion
    /\b(?:decreased|limited|reduced|restricted|painful)\s+rom(?:\s+(?:of\s+)?[a-z\- ]{1,25})?/gi,
    /\brom\s+(?:decreased|limited|reduced|restricted|painful)(?:[\s\S]{0,30}?)(?=[.,;]|$)/gi,
    // Swelling, edema, effusion
    /\b(?:\d\+?\s*pitting\s+)?edema\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    /\b(?:swelling|effusion|joint effusion|periorbital\s+edema)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Skin / superficial
    /\b(?:erythema|warmth|ecchymosis|bruising|abrasion|laceration|hematoma|contusion|petechiae)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Crepitus / clicking
    /\b(?:crepitus|grinding|clicking|popping)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Special tests (orthopedic) — positive findings
    /(?:\bpositive\b|\+)\s*(?:spurling|mcmurray|lachman|drawer|slr|straight leg raise|hawkins|neer|finkelstein|phalen|tinel|impingement|apprehension|patellar grind|valgus|varus)(?:[\s\S]{0,25}?)(?=[.,;]|$)/gi,
    // Strength deficits
    /\b(?:strength|weakness)\s*(?:graded|of)?\s*\d\s*\/\s*5\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    /\b\d\s*\/\s*5\s+(?:strength|weakness|in|on|of)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Gait / posture
    /\b(?:antalgic gait|abnormal gait|wide-based gait|trendelenburg|positive trendelenburg)\b[\s\S]{0,25}?(?=[.,;]|$)/gi,
    // Reflexes
    /\b(?:hyperreflexia|hyporeflexia|absent reflexes|diminished reflexes|reflexes \d\+?)\b[\s\S]{0,25}?(?=[.,;]|$)/gi,
    // Auscultation / cardio-pulm abnormals
    /\b(?:wheez(?:ing|es)|rales|rhonchi|crackles|rub|murmur|s3 gallop|s4 gallop|irregular(?:ly)? irregular)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Abdomen
    /\b(?:guarding|rebound tenderness|distension|distention|hepatomegaly|splenomegaly|positive murphy|positive mcburney|cva tenderness)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
    // Neuro / sensory
    /\b(?:numbness|paresthesias?|decreased sensation|loss of sensation|hypoesthesia)\b[\s\S]{0,40}?(?=[.,;]|$)/gi,
    // ENT / Eye
    /\b(?:erythematous (?:tm|tympanic|throat|pharynx)|tm bulging|tm dull|conjunctival injection|tonsillar exudate|cobblestoning)\b[\s\S]{0,30}?(?=[.,;]|$)/gi,
  ];

  for (const re of patterns) {
    const matches = lower.match(re);
    if (!matches) continue;
    for (const m of matches) {
      // Re-locate in original-case text to preserve casing of body parts
      const idx = lower.indexOf(m);
      if (idx >= 0) push(text.slice(idx, idx + m.length));
    }
  }

  // Cap to first 5 findings to keep the input box readable
  return findings.slice(0, 5).join(', ');
};




/**
 * stripEmbeddedSignature — defensive scrub for the "All — no sig" / "Note — no sig"
 * copy paths. Sometimes the LLM tail-appends an attestation/sig at the end of
 * clinical_note (or the /merge-notes flow inherits the sig from Note A and bakes
 * it into the merged note body). Both cause "no sig" buttons to still push sig
 * content into the EHR. This strips known trailing signature patterns so the
 * "no sig" promise is honored regardless of upstream root cause.
 *
 * Conservative: only strips from the LAST occurrence of an attestation/sig
 * marker to end-of-text. Leaves the body untouched.
 */
export const stripEmbeddedSignature = (text) => {
  if (!text || typeof text !== 'string') return text;
  let out = text;

  // Markers that, once seen near end-of-note, indicate the start of a
  // baked-in signature/attestation block. Patterns are case-insensitive and
  // anchored to the start of a line so we don't snip mid-sentence A/P content.
  const markers = [
    /^I spent (a total of )?\d+(\s|\u00A0)*(minutes|min)\b[\s\S]*$/im,
    /^This visit was completed (telephonically|via video)\b[\s\S]*$/im,
    /^Note:\s*Portions of this note were made using Dragon\b[\s\S]*$/im,
    /^Date of Service:\s*[\s\S]*$/im,
    /^Note Completed:\s*[\s\S]*$/im,
    /^_{8,}\s*\n?\(?Provider Signature Block\)?[\s\S]*$/im,
    /^\(?Provider Signature Block\)?[\s\S]*$/im,
    /^\[PROVIDER SIGNATURE BLOCK\][\s\S]*$/im,
    /^\/\/SIGNED\/\/[\s\S]*$/im,
    /^Electronically signed by[\s\S]*$/im,
  ];

  // Find the EARLIEST marker hit — that's where the embedded sig begins.
  let earliest = -1;
  for (const re of markers) {
    const m = out.match(re);
    if (m && m.index !== undefined) {
      if (earliest === -1 || m.index < earliest) earliest = m.index;
    }
  }
  if (earliest >= 0) {
    out = out.slice(0, earliest).replace(/\n{3,}$/, '\n').trimEnd();
  }
  return out;
};
